/**
 * Client media hosted on Supabase storage.
 * `mediaUrl` returns the original file, `mediaThumb` returns a resized/compressed
 * render used for cards, heroes, and gallery thumbnails.
 */

const OBJECT_BASE =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/yessica-cleaning-and-painting-services-llc";
const RENDER_BASE =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/yessica-cleaning-and-painting-services-llc";

export const mediaUrl = (file: string) => `${OBJECT_BASE}/images/${file}`;

export const videoUrl = (file: string) => `${OBJECT_BASE}/videos/${file}`;

export const mediaThumb = (file: string, width = 480, height = 480, quality = 50) =>
  `${RENDER_BASE}/images/${file}?width=${width}&height=${height}&resize=cover&quality=${quality}`;

/**
 * Wide crop for hero banners. `width` defaults to 1920 — pass the source's real
 * width instead for portrait originals, so we request a true-resolution strip
 * rather than asking the CDN to upscale.
 */
export const mediaHero = (file: string, width = 1920) =>
  `${RENDER_BASE}/images/${file}?width=${width}&height=${Math.round(width * 9 / 16)}&resize=cover&quality=72`;

/** Widths offered for the responsive hero so phones don't fetch the 1920px crop. */
export const HERO_WIDTHS = [640, 960, 1280, 1920] as const;

/** `srcset` string for a hero image. Keep in sync with the build-time preload. */
export const mediaHeroSrcSet = (file: string, widths: readonly number[] = HERO_WIDTHS) =>
  widths.map((w) => `${mediaHero(file, w)} ${w}w`).join(", ");

/** Origin that serves all client media — used for a preconnect hint. */
export const MEDIA_ORIGIN = "https://sbdjxsxpkgngbkdabugx.supabase.co";

/** Card crop used across service, blog, and location cards. */
export const mediaCard = (file: string) => mediaThumb(file, 900, 675, 65);

/**
 * Named shots from the client's own project photos, verified individually.
 *
 * Resolution matters here: only the images in `wide` are large-format landscape
 * originals (4032x3024 or 5712x4284) and safe to use as full-bleed heroes.
 * Everything under `card` is 1200x1600 portrait — sharp at card size, but it
 * would upscale and soften if stretched across a 1920px hero.
 */
export const photos = {
  /** Freshly painted interior — grey walls, white trim. 4032x3024. */
  interiorPaintWide: "1783969880711-618-IMG_2413.jpeg",
  /** Painted interior wall, window trim and upper cabinet. 4032x3024. */
  interiorPaintRoom: "1783969872678-799-IMG_2406.jpeg",
  /** Oven cleaned to bare metal. 5712x4284. */
  cleanOven: "1783970400306-266-IMG_8380.jpeg",

  /** Freshly repainted house exterior, entry and trim. 1200x1600 — card only. */
  exteriorHouse: "1783969915201-567-56d4e116-6187-4ccb-85cc-1922a3e748ef.jpeg",
  /** Repainted white lap siding against blue sky. 1200x1600 — card only. */
  exteriorSiding: "1783969913212-458-ee7715c0-6ebf-4e90-bfbf-8adf13a236b2.jpeg",
  /** Repainted deck, stairs and railings. 1200x1600 — card only. */
  deckPaint: "1783969910295-145-9dee9500-565f-4067-98cd-f991b130cf3b.jpeg",
  /** Gloss black stair railing and spindles. 1200x1600 — card only. */
  trimRailing: "1783969920249-446-cda70615-b2bf-4f6d-9f3d-c7d32130fca8.jpeg",
  /** Crew member pressure washing a driveway mid-job. 1200x1600 — card only. */
  pressureWashDriveway: "1783969957064-81-61498c8b-2687-4b26-8bfd-aedb31ab5632.jpeg",
} as const;
